/**
 * Reports.jsx — School-admin reporting dashboard.
 *
 * Provides:
 *  - Filter bar: session picker, date range, granularity (day/week/month) toggle
 *  - KPI cards: Expected, Collected (in range), Pending, % Collected
 *  - Collection trend bar chart
 *  - Per-class table with collected vs pending per class
 *  - Per-collector table (who collected how much)
 *  - CSV export buttons (payments + by-class + by-collector) — all server-rendered
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  ChartBar,
  DownloadSimple,
  Calendar,
  CurrencyInr,
  CheckCircle,
  Hourglass,
  Receipt,
  TrendUp,
  Printer,
  ArrowsLeftRight,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import CollectionReconReportCard from "@/pages/schooladmin/CollectionReconReportCard";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function buildQuery(params) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== "__all__") usp.set(k, v);
  });
  return usp.toString();
}

async function downloadCsv(url, filename) {
  try {
    const token = tokenStore.get();
    const res = await fetch(`${API_BASE}${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(obj);
  } catch (e) {
    toast.error(`Download failed: ${e.message}`);
  }
}

export default function Reports() {
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("__all__"); // "__all__" = aggregate, else drilldown
  const [paymentMode, setPaymentMode] = useState("__all__");
  const [periodMonth, setPeriodMonth] = useState("");
  const [dateFrom, setDateFrom] = useState(todayISO(-30));
  const [dateTo, setDateTo] = useState(todayISO(0));
  const [granularity, setGranularity] = useState("day");

  const [summary, setSummary] = useState(null);
  const [byClass, setByClass] = useState([]);
  const [byCollector, setByCollector] = useState([]);
  const [trend, setTrend] = useState([]);
  const [classDetail, setClassDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isDrilldown = classId !== "__all__";

  const baseFilter = useMemo(() => ({
    session_id: sessionId || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    payment_mode: paymentMode !== "__all__" ? paymentMode : undefined,
    period_month: periodMonth || undefined,
  }), [sessionId, dateFrom, dateTo, paymentMode, periodMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const q = buildQuery(baseFilter);
      const tq = buildQuery({ ...baseFilter, granularity });
      if (isDrilldown) {
        const cdQ = buildQuery({ ...baseFilter, class_id: classId });
        const [s, cd] = await Promise.all([
          api.get(`/school/reports/summary${q ? `?${q}` : ""}`),
          api.get(`/school/reports/class-detail?${cdQ}`),
        ]);
        setSummary(s.data);
        setClassDetail(cd.data);
      } else {
        const [s, bc, bk, tr] = await Promise.all([
          api.get(`/school/reports/summary${q ? `?${q}` : ""}`),
          api.get(`/school/reports/by-class${q ? `?${q}` : ""}`),
          api.get(`/school/reports/by-collector${q ? `?${q}` : ""}`),
          api.get(`/school/reports/collection-trend${tq ? `?${tq}` : ""}`),
        ]);
        setSummary(s.data);
        setByClass(bc.data);
        setByCollector(bk.data);
        setTrend(tr.data);
        setClassDetail(null);
      }
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [baseFilter, granularity, isDrilldown, classId]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: ss }, { data: cs }] = await Promise.all([
          api.get("/school/sessions"),
          api.get("/school/classes"),
        ]);
        setSessions(ss);
        setClasses(cs);
        const active = ss.find((s) => s.is_active);
        if (active) setSessionId(active.id);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!sessionId) return; // wait for session list
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load, sessionId]);

  const setRange = (preset) => {
    if (preset === "today") { setDateFrom(todayISO(0)); setDateTo(todayISO(0)); }
    else if (preset === "7d") { setDateFrom(todayISO(-6)); setDateTo(todayISO(0)); }
    else if (preset === "30d") { setDateFrom(todayISO(-29)); setDateTo(todayISO(0)); }
    else if (preset === "90d") { setDateFrom(todayISO(-89)); setDateTo(todayISO(0)); }
    else if (preset === "all") { setDateFrom(""); setDateTo(""); }
  };

  return (
    <div className="space-y-6 print:space-y-3" data-testid="reports-page">
      <div className="flex items-start justify-between gap-3 print:block">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Insights</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1 flex items-center gap-2">
            <ChartBar size={28} weight="duotone" />
            {isDrilldown ? `Class report: ${classDetail?.class_name || "…"}` : "Reports"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isDrilldown
              ? "Per-student paid / partial / pending breakdown for the selected class. Use this to audit teacher collections, spot duplicates and verify pending fees."
              : "Track collection vs pending across classes and collectors. Filter by date range and export to CSV/Excel."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="gap-1.5 print:hidden"
            data-testid="reports-monthly-verification-link"
          >
            <a href="/reports/monthly-verification">Monthly Verification</a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="gap-1.5 print:hidden"
            data-testid="reports-concessions-link"
          >
            <a href="/reports/concessions">Concessions</a>
          </Button>
          <Button
            variant="outline"
            className="gap-1.5 print:hidden"
            onClick={() => window.print()}
            data-testid="reports-print-btn"
          >
            <Printer size={14} /> Print
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-slate-200 bg-white rounded-lg p-4 space-y-3 print:hidden" data-testid="reports-filters">
        <div className="flex items-end flex-wrap gap-3">
          <div className="min-w-[180px]">
            <Label className="text-xs">Academic session</Label>
            <Select value={sessionId} onValueChange={setSessionId}>
              <SelectTrigger className="mt-1 h-9" data-testid="reports-session-select">
                <SelectValue placeholder="Pick a session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}{s.is_active ? <span className="text-emerald-600 ml-2">· active</span> : null}
                    {s.is_archived ? <span className="text-amber-600 ml-2">· archived</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Class</Label>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger className="mt-1 h-9" data-testid="reports-class-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All classes</SelectItem>
                {classes.filter((c) => !sessionId || c.session_id === sessionId).map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`reports-class-${c.id}`}>
                    {c.name}{c.section ? ` - ${c.section}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={paymentMode} onValueChange={setPaymentMode}>
              <SelectTrigger className="mt-1 h-9 w-32" data-testid="reports-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All modes</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Month</Label>
            <Input
              type="month"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
              className="mt-1 h-9 w-36"
              data-testid="reports-period-month"
            />
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 h-9 w-40"
              data-testid="reports-date-from"
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 h-9 w-40"
              data-testid="reports-date-to"
            />
          </div>
          {!isDrilldown && (
            <div>
              <Label className="text-xs">Granularity</Label>
              <Select value={granularity} onValueChange={setGranularity}>
                <SelectTrigger className="mt-1 h-9 w-32" data-testid="reports-granularity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-1.5 ml-auto" data-testid="reports-presets">
            {[
              ["today", "Today"], ["7d", "7d"], ["30d", "30d"],
              ["90d", "90d"], ["all", "All time"],
            ].map(([k, lbl]) => (
              <Button
                key={k}
                size="sm"
                variant="outline"
                className="h-9 text-xs"
                onClick={() => setRange(k)}
                data-testid={`reports-preset-${k}`}
              >
                {lbl}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-kpis">
        <KpiCard
          icon={<CurrencyInr size={20} weight="duotone" />}
          label="Expected (session)"
          value={summary ? formatINR(summary.expected_total) : "—"}
          sub={summary ? `${summary.students_total} active students` : ""}
          tone="slate"
          testid="kpi-expected"
        />
        <KpiCard
          icon={<CheckCircle size={20} weight="duotone" />}
          label="Collected (in range)"
          hint="CAPPED: money received in range that maps to genuine fee liability. Excludes over-collection. The 'Receipts collected by staff' table below and the trend chart show RAW receipts — they may exceed this KPI by exactly the over-collection amount."
          value={summary ? formatINR(summary.collected_in_range) : "—"}
          sub={summary ? `${summary.receipts_in_range} receipts` : ""}
          tone="emerald"
          testid="kpi-collected"
        />
        <KpiCard
          icon={<Hourglass size={20} weight="duotone" />}
          label="Pending (overall)"
          hint="CAPPED-derived: liability minus capped collection (max 0). The Defaulters page sums per-student pending (each clamped at 0) — these two numbers may differ when any student has over-paid."
          value={summary ? formatINR(summary.pending) : "—"}
          sub={summary ? `${summary.students_due} due · ${summary.students_partial} partial` : ""}
          tone="amber"
          testid="kpi-pending"
        />
        <KpiCard
          icon={<TrendUp size={20} weight="duotone" />}
          label="% Collected (all-time)"
          value={summary ? `${summary.pct_collected.toFixed(1)}%` : "—"}
          sub={summary ? `${summary.students_paid} fully paid` : ""}
          tone="blue"
          testid="kpi-pct"
        />
      </div>

      {/* Phase 30K-pre — RAW ↔ CAPPED reconciliation footer.
          Surfaces both definitions of "collected" side-by-side so operators
          understand why the By-Collector / Payments / Trend totals may
          exceed the "Collected (in range)" KPI when over-collection exists.
          Hidden in drilldown view (per-class) since that view uses its own
          context. */}
      {!isDrilldown && summary && (
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-600"
          data-testid="reports-reconciliation-footer"
        >
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Receipts received (RAW)</span>
            <span className="font-mono font-semibold text-slate-800" data-testid="recon-raw">
              {formatINR(summary.collected_all_time || 0)}
            </span>
          </div>
          <div className="text-slate-300">=</div>
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-emerald-700 uppercase tracking-wider text-[10px]">Maps to liability (CAPPED)</span>
            <span className="font-mono font-semibold text-emerald-800" data-testid="recon-capped">
              {formatINR(summary.collected_capped || 0)}
            </span>
          </div>
          <div className="text-slate-300">+</div>
          <div className="flex items-center gap-1.5">
            <span
              className="font-semibold text-amber-700 uppercase tracking-wider text-[10px] cursor-help"
              title="Money received beyond what the active fee structures account for (e.g. paid more than the monthly tuition, or paid for a since-removed fee head). Treated as 'received but not against liability'."
            >
              Over-collection ⓘ
            </span>
            <span className="font-mono font-semibold text-amber-800" data-testid="recon-over">
              {formatINR(summary.over_collection || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Trend chart (only in aggregate mode) */}
      {!isDrilldown && (
      <>
      <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="reports-trend">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1">
              <span>Collection trend</span>
              <span
                title="RAW receipts grouped by date. Σ of all bars equals 'Receipts received (RAW)' in the reconciliation footer, NOT the 'Collected (in range)' KPI (which is CAPPED)."
                className="cursor-help text-slate-400 normal-case tracking-normal"
              >
                ⓘ
              </span>
            </div>
            <div className="text-sm text-slate-700 mt-0.5">
              {trend.length} {granularity}-buckets in selected range · RAW receipts
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => downloadCsv(
              `/school/reports/exports/payments.csv?${buildQuery(baseFilter)}`,
              `payments-${dateFrom || "all"}--${dateTo || "all"}.csv`,
            )}
            data-testid="reports-export-payments-btn"
          >
            <DownloadSimple size={14} /> Payments CSV
          </Button>
        </div>
        <div className="h-64" data-testid="reports-trend-chart">
          {trend.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <Calendar size={28} className="mb-1" />
              <div className="text-sm">No collections in this range.</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" tickFormatter={(v) => formatINR(v)} width={80} />
                <Tooltip
                  formatter={(v, k) => (k === "amount" ? formatINR(v) : v)}
                  contentStyle={{ borderRadius: 6, fontSize: 12 }}
                />
                <Bar dataKey="amount" fill="#1E3A8A" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* By Class */}
      <div className="border border-slate-200 bg-white rounded-lg" data-testid="reports-by-class">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Per class</div>
            <div className="text-sm text-slate-700 mt-0.5">Expected vs collected vs pending across {byClass.length} classes</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => downloadCsv(
              `/school/reports/exports/by-class.csv?${buildQuery(baseFilter)}`,
              `by-class-${dateFrom || "all"}--${dateTo || "all"}.csv`,
            )}
            data-testid="reports-export-by-class-btn"
          >
            <DownloadSimple size={14} /> CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reports-by-class-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Class</th>
                <th className="text-right font-semibold px-4 py-3">Students</th>
                <th className="text-right font-semibold px-4 py-3">Expected</th>
                <th className="text-right font-semibold px-4 py-3">Collected (range)</th>
                <th className="text-right font-semibold px-4 py-3">Collected (total)</th>
                <th className="text-right font-semibold px-4 py-3">Pending</th>
                <th className="text-right font-semibold px-4 py-3">% Coll.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && byClass.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : byClass.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="reports-by-class-empty">
                    No classes in this session yet.
                  </td>
                </tr>
              ) : byClass.map((c) => (
                <tr key={c.class_id} className="hover:bg-slate-50" data-testid={`reports-by-class-row-${c.class_id}`}>
                  <td className="px-4 py-3 text-slate-900 font-medium">{c.class_name}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">{c.students}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{formatINR(c.expected)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatINR(c.collected_in_range)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{formatINR(c.collected_all_time)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-700">{formatINR(c.pending)}</td>
                  <td className="px-4 py-3 text-right">
                    <PercentBar pct={c.pct_collected} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* By Collector */}
      <div className="border border-slate-200 bg-white rounded-lg" data-testid="reports-by-collector">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1">
              <span>Per collector</span>
              <span
                title="RAW receipts received per staff member. Σ of 'Total' column equals 'Receipts received (RAW)' in the reconciliation footer above — NOT the 'Collected (in range)' KPI (which is CAPPED). Useful for cash-handover reconciliation."
                className="cursor-help text-slate-400 normal-case tracking-normal"
              >
                ⓘ
              </span>
            </div>
            <div className="text-sm text-slate-700 mt-0.5">Receipts collected by staff in selected range · RAW totals</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => downloadCsv(
              `/school/reports/exports/by-collector.csv?${buildQuery(baseFilter)}`,
              `by-collector-${dateFrom || "all"}--${dateTo || "all"}.csv`,
            )}
            data-testid="reports-export-by-collector-btn"
          >
            <DownloadSimple size={14} /> CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reports-by-collector-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Collector</th>
                <th className="text-left font-semibold px-4 py-3">Role</th>
                <th className="text-right font-semibold px-4 py-3">Receipts</th>
                <th className="text-right font-semibold px-4 py-3" title="RAW Σ amount_paid">
                  Total (raw ₹)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && byCollector.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : byCollector.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="reports-by-collector-empty">
                    <Receipt size={28} className="mx-auto text-slate-300 mb-1" />
                    No collections in this range.
                  </td>
                </tr>
              ) : byCollector.map((c, i) => (
                <tr key={`${c.collector_id}-${i}`} className="hover:bg-slate-50" data-testid={`reports-by-collector-row-${c.collector_id}`}>
                  <td className="px-4 py-3 text-slate-900 font-medium">{c.collector_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 capitalize">{c.collector_role.replace("_", " ")}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{c.count}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{formatINR(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Class drilldown view */}
      {isDrilldown && classDetail && (
        <div className="space-y-4">
          {classDetail.duplicates_detected > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-sm" data-testid="reports-duplicate-warning">
              <WarningCircle size={20} weight="duotone" />
              <span>
                <strong>{classDetail.duplicates_detected}</strong> potential duplicate payment{classDetail.duplicates_detected === 1 ? "" : "s"} detected
                (same student + fee head + month seen more than once). Review the rows marked <span className="font-mono px-1.5 bg-amber-200 rounded text-[11px]">DUP</span>.
              </span>
            </div>
          )}

          <div className="border border-slate-200 bg-white rounded-lg" data-testid="reports-class-detail">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between print:border-black">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                  Per student · {classDetail.class_name}
                </div>
                <div className="text-sm text-slate-700 mt-0.5">
                  {classDetail.students_total} students · Expected {formatINR(classDetail.expected_total)} · Collected {formatINR(classDetail.collected_all_time)} · Pending {formatINR(classDetail.pending_total)}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 print:hidden"
                onClick={() => downloadCsv(
                  `/school/reports/exports/class-detail.csv?${buildQuery({ ...baseFilter, class_id: classId })}`,
                  `class-detail-${classDetail.class_name.replace(/\s+/g, "_")}-${dateFrom || "all"}.csv`,
                )}
                data-testid="reports-export-class-detail-btn"
              >
                <DownloadSimple size={14} /> Excel/CSV
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-class-detail-table">
                <thead className="bg-slate-50">
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="text-left font-semibold px-4 py-3">Student</th>
                    <th className="text-left font-semibold px-4 py-3">Adm No.</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="text-right font-semibold px-4 py-3">Expected</th>
                    <th className="text-right font-semibold px-4 py-3">Paid</th>
                    <th className="text-right font-semibold px-4 py-3">Balance</th>
                    <th className="text-right font-semibold px-4 py-3">Receipts</th>
                    <th className="text-left font-semibold px-4 py-3">Modes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {classDetail.students.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No students in this class yet.</td></tr>
                  ) : classDetail.students.map((s) => {
                    const tone =
                      s.status === "paid" ? "bg-emerald-50" :
                      s.status === "partial" ? "bg-amber-50" :
                      s.status === "due" ? "bg-red-50" : "";
                    return (
                      <tr
                        key={s.student_id}
                        className={`${tone} hover:bg-opacity-80`}
                        data-testid={`reports-class-detail-row-${s.student_id}`}
                      >
                        <td className="px-4 py-3 text-slate-900 font-medium">
                          {s.name}
                          {s.has_duplicates && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 bg-amber-200 text-amber-900 text-[10px] font-mono rounded" data-testid={`reports-class-detail-dup-${s.student_id}`}>
                              DUP
                            </span>
                          )}
                          {!s.is_active && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 bg-slate-200 text-slate-700 text-[10px] uppercase rounded">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-xs">{s.admission_no}</td>
                        <td className="px-4 py-3">
                          <StatusPill status={s.status} />
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-900">{formatINR(s.expected)}</td>
                        <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatINR(s.paid)}</td>
                        <td className="px-4 py-3 text-right font-mono text-amber-700">{formatINR(s.balance)}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-700">{s.receipts}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {(s.modes || []).map((m) => m.toUpperCase()).join(", ") || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Phase 30L — Collection & Reconciliation Reporting (feature-flag
          gated). Renders nothing if the tenant flag is OFF. Hidden in
          class-drilldown view since it's a period-wide reconciliation tool. */}
      {!isDrilldown && <CollectionReconReportCard />}

      {/* Phase 30 — Audit Tools (school-admin only diagnostic CSV exports).
          Visible only to school_admin (the Reports page itself is already
          ProtectedRoute roles={["school_admin"]}, so this card is reached
          only by admins; both backend endpoints additionally enforce
          require_school_admin → 403 for any non-admin). */}
      <AuditExportsCard sessionId={sessionId} />
    </div>
  );
}

function AuditExportsCard({ sessionId }) {
  const [busy, setBusy] = useState(null);
  const download = async (endpoint, filename) => {
    if (!sessionId) {
      toast.error("Pick a session first");
      return;
    }
    setBusy(endpoint);
    await downloadCsv(
      `/school/admin/audit-exports/${endpoint}.csv?session_id=${sessionId}`,
      filename,
    );
    setBusy(null);
  };
  const downloadJson = async (endpoint, filename) => {
    if (!sessionId) {
      toast.error("Pick a session first");
      return;
    }
    setBusy(endpoint);
    await downloadCsv(
      `/school/admin/audit-exports/${endpoint}.json?session_id=${sessionId}`,
      filename,
    );
    setBusy(null);
  };
  return (
    <div
      className="p-5 border border-slate-200 bg-white rounded-lg"
      data-testid="audit-tools-card"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-xl font-medium font-heading flex items-center gap-2">
            <WarningCircle size={18} weight="duotone" className="text-amber-600" />
            Audit Tools
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Read-only diagnostic CSVs for KPI reconciliation. Use these to
            verify whether a manual-vs-system discrepancy stems from advance
            payments, annual one-time fees, or a real calculation issue.
            Admin-only.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mt-3">
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() => download("advance-payments", "advance-payments.csv")}
          data-testid="audit-advance-csv-btn"
        >
          <DownloadSimple size={14} className="mr-2" />
          {busy === "advance-payments" ? "Preparing…" : "Advance Payments (CSV)"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() =>
            download("class-month-matrix", "class-month-matrix.csv")
          }
          data-testid="audit-class-month-csv-btn"
        >
          <DownloadSimple size={14} className="mr-2" />
          {busy === "class-month-matrix"
            ? "Preparing…"
            : "Class × Month Matrix (CSV)"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() =>
            downloadJson(
              "kpi-reconciliation",
              `kpi-reconciliation-${new Date().toISOString().slice(0, 10)}.json`,
            )
          }
          data-testid="audit-kpi-reconciliation-btn"
        >
          <DownloadSimple size={14} className="mr-2" />
          {busy === "kpi-reconciliation"
            ? "Preparing…"
            : "KPI Reconciliation (JSON)"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() =>
            downloadJson(
              "class-transfer-mismatch",
              `class-transfer-mismatch-${new Date().toISOString().slice(0, 10)}.json`,
            )
          }
          data-testid="audit-class-transfer-mismatch-btn"
        >
          <DownloadSimple size={14} className="mr-2" />
          {busy === "class-transfer-mismatch"
            ? "Preparing…"
            : "Class Transfer Mismatch (JSON)"}
        </Button>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: { bg: "bg-emerald-100 text-emerald-800", label: "Paid" },
    partial: { bg: "bg-amber-100 text-amber-800", label: "Partial" },
    due: { bg: "bg-red-100 text-red-800", label: "Pending" },
    no_fee: { bg: "bg-slate-100 text-slate-600", label: "No fee" },
  };
  const m = map[status] || map.no_fee;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${m.bg}`} data-testid={`reports-class-detail-status-${status}`}>
      {m.label}
    </span>
  );
}

function KpiCard({ icon, label, value, sub, tone, testid, hint }) {
  const tones = {
    slate: "border-slate-200 text-slate-700",
    emerald: "border-emerald-200 text-emerald-700",
    amber: "border-amber-200 text-amber-700",
    blue: "border-blue-200 text-blue-900",
  };
  return (
    <div className={`p-5 border bg-white rounded-lg min-w-0 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold opacity-70">
        {icon}
        <span>{label}</span>
        {hint && (
          <span
            title={hint}
            className="cursor-help text-slate-400 hover:text-slate-600 normal-case tracking-normal"
            data-testid={testid ? `${testid}-hint` : undefined}
          >
            ⓘ
          </span>
        )}
      </div>
      {/* Phase 27I.5 — mobile-safe KPI value rendering.
          - text-xl on mobile (20px) → text-2xl at sm → text-3xl at md+
          - break-words so any future amount past 1cr wraps cleanly
            inside the card boundary instead of overflowing
          - min-w-0 on the card lets the grid cell shrink below content
            width so the wrap actually triggers
          - Exact rupee precision is fully preserved (no compact Cr/L). */}
      <div className="text-xl sm:text-2xl md:text-3xl font-bold font-heading mt-2 font-mono text-slate-900 break-words">{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function PercentBar({ pct }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const color = clamped >= 80 ? "bg-emerald-500" : clamped >= 40 ? "bg-blue-500" : "bg-amber-500";
  return (
    <div className="inline-flex items-center gap-2 min-w-[120px] justify-end">
      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${clamped}%` }} />
      </div>
      <div className="font-mono text-xs text-slate-700 w-10 text-right">{clamped.toFixed(0)}%</div>
    </div>
  );
}
